import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CONFIG, NAV_LINKS, goToLeadForm } from "@/config";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const cta = () => {
    setOpen(false);
    goToLeadForm(navigate);
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-navy ${isActive ? "text-navy" : "text-navy/70"}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "border-b border-navy/5 bg-white/80 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-2 focus:outline-none" aria-label="Auxieron home">
          <img src={CONFIG.logo} alt="Auxieron Pvt. Ltd. logo" className="h-11 w-auto sm:h-12" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={linkCls}>
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded-full bg-gold" />}
                </span>
              )}
            </NavLink>
          ))}
          <button
            data-testid="header-cta"
            onClick={cta}
            className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d2c56]"
          >
            Get Started
          </button>
        </nav>

        <button
          data-testid="menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full p-2 text-navy lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-t border-navy/5 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `rounded-xl px-3 py-3 text-left text-base font-medium transition-colors hover:bg-navy/5 ${
                      isActive ? "bg-navy/5 text-navy" : "text-navy/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-header-cta"
                onClick={cta}
                className="mt-2 rounded-full bg-gold px-5 py-3.5 text-center text-base font-bold text-navy"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
